"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Phone,
  Menu,
  X,
  ChevronDown,
  Shield,
  Flame,
  MessageCircle,
  CalendarCheck,
} from "lucide-react";
import { SITE, NAV_LINKS } from "@/lib/siteData";
import EagleLogo from "@/components/EagleLogo";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const toggleMobileMenu = useCallback(() => setMobileOpen((p) => !p), []);
  const closeMobileMenu = useCallback(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, []);

  return (
    <header ref={headerRef} className="sticky top-0 z-50">
      {/* Top Bar — bright gold band (matches original site identity) */}
      <div className="bg-[#E8BD4E] text-[#0A0A0A] text-sm border-b border-[#B98002]/40">
        <div className="mx-auto max-w-7xl px-4 py-2 flex items-center justify-between">
          <div className="hidden sm:flex items-center gap-4">
            <span className="font-display italic text-[13px] text-[#5A3F00] tracking-wide">
              Est. {SITE.founded}
            </span>
            <span className="text-[#5A3F00]/40" aria-hidden="true">·</span>
            <span className="flex items-center gap-1.5 text-[#0A0A0A]">
              <Shield className="h-3.5 w-3.5 text-[#5A3F00]" aria-hidden="true" />
              <span className="text-xs tracking-wide font-medium">Licensed &amp; Insured</span>
            </span>
            <span className="hidden md:inline text-[#5A3F00]/40" aria-hidden="true">·</span>
            <span className="hidden md:flex items-center gap-1.5 text-[#0A0A0A]">
              <Flame className="h-3.5 w-3.5 text-[#5A3F00]" aria-hidden="true" />
              <span className="text-xs tracking-wide font-medium">Owner-Operated</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/contact"
              className="sm:hidden flex items-center gap-1.5 text-[#0A0A0A] hover:text-[#5A3F00] transition-colors min-h-[48px]"
              aria-label="Send us a message"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-xs font-medium">Message</span>
            </Link>
            <a
              href={SITE.phoneHref}
              className="flex items-center gap-1.5 font-semibold text-[#0A0A0A] hover:text-[#5A3F00] transition-colors min-h-[48px] sm:min-h-0"
            >
              <Phone className="h-4 w-4" />
              <span className="tracking-wide">{SITE.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation — white background, dark ink text, gold underline accents */}
      <nav className="bg-[#FFFFFF] relative shadow-sm">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#E8BD4E]/80 to-transparent" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 flex items-center justify-between h-20 lg:h-24">
          <Link href="/" className="flex items-center" onClick={closeMobileMenu}>
            <EagleLogo className="h-14 lg:h-16 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className="nav-link flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-[#0A0A0A] hover:text-[#5A3F00] transition-colors min-h-[48px] uppercase tracking-[0.18em]"
                  >
                    {link.label}
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180 text-[#5A3F00]" : ""}`}
                    />
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-1 w-80 bg-[#FFFFFF] border border-[#E8BD4E]/50 rounded-sm shadow-xl py-2 z-50">
                      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#E8BD4E] to-transparent" aria-hidden="true" />
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          href={child.href}
                          onClick={() => setServicesOpen(false)}
                          className="block px-4 py-2.5 text-sm text-[#0A0A0A] hover:text-[#5A3F00] hover:bg-[#FFF7DD] transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  href={link.href}
                  data-active={pathname === link.href || undefined}
                  className="nav-link px-4 py-2 text-[12px] font-semibold text-[#0A0A0A] hover:text-[#5A3F00] transition-colors min-h-[48px] flex items-center uppercase tracking-[0.18em]"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/appointment"
              className="cta-gold-outline flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium text-[12px] min-h-[44px] uppercase tracking-[0.16em] whitespace-nowrap shrink-0"
            >
              <CalendarCheck className="h-4 w-4 shrink-0" />
              Schedule
            </Link>
            <a
              href={SITE.phoneHref}
              className="cta-gold flex items-center gap-2 px-5 py-2.5 rounded-sm font-bold min-h-[44px] text-[12px] uppercase tracking-[0.16em] whitespace-nowrap shrink-0"
            >
              <Phone className="h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">{SITE.phone}</span>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            type="button"
            onClick={toggleMobileMenu}
            className="lg:hidden flex items-center justify-center w-12 h-12 rounded-sm text-[#0A0A0A] hover:bg-[#FFF7DD] active:bg-[#FFEFB6] transition-colors border border-[#E8BD4E]/50"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <>
            <div
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
              onClick={closeMobileMenu}
              aria-hidden="true"
            />
            <div className="lg:hidden fixed inset-x-0 top-0 bottom-0 z-50 bg-[#FFFFFF] overflow-y-auto mobile-menu-enter pt-4">
              <div className="flex items-center justify-between px-4 mb-4 pb-4 border-b border-[#E8BD4E]/50">
                <Link href="/" className="flex items-center" onClick={closeMobileMenu}>
                  <EagleLogo className="h-12 w-auto" />
                </Link>
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center w-12 h-12 rounded-sm text-[#0A0A0A] hover:bg-[#FFF7DD] active:bg-[#FFEFB6] transition-colors border border-[#E8BD4E]/50"
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="px-4 py-2 flex flex-col gap-1">
                {NAV_LINKS.map((link) =>
                  link.children ? (
                    <div key={link.label}>
                      <button
                        type="button"
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className="flex items-center justify-between w-full px-4 py-3 text-[13px] font-semibold text-[#0A0A0A] hover:text-[#5A3F00] hover:bg-[#FFF7DD] active:bg-[#FFEFB6] rounded-sm min-h-[48px] transition-colors uppercase tracking-[0.18em]"
                      >
                        {link.label}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180 text-[#5A3F00]" : ""}`}
                        />
                      </button>
                      {mobileServicesOpen && (
                        <div className="ml-4 flex flex-col gap-1 mt-1 border-l border-[#E8BD4E]/50 pl-3">
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              href={child.href}
                              onClick={closeMobileMenu}
                              className="px-4 py-2.5 text-sm text-slate-700 hover:text-[#5A3F00] hover:bg-[#FFF7DD] active:bg-[#FFEFB6] rounded-sm min-h-[48px] flex items-center transition-colors"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={link.label}
                      href={link.href}
                      onClick={closeMobileMenu}
                      className="px-4 py-3 text-[13px] font-semibold text-[#0A0A0A] hover:text-[#5A3F00] hover:bg-[#FFF7DD] active:bg-[#FFEFB6] rounded-sm min-h-[48px] flex items-center transition-colors uppercase tracking-[0.18em]"
                    >
                      {link.label}
                    </Link>
                  )
                )}

                <div className="mt-6 flex flex-col gap-3 border-t border-[#E8BD4E]/50 pt-6">
                  <a
                    href={SITE.phoneHref}
                    className="cta-gold flex items-center justify-center gap-2 px-6 py-4 rounded-sm font-bold min-h-[56px] text-base uppercase tracking-[0.14em]"
                  >
                    <Phone className="h-5 w-5" />
                    Call {SITE.phone}
                  </a>
                  <Link
                    href="/appointment"
                    onClick={closeMobileMenu}
                    className="cta-gold-outline flex items-center justify-center gap-2 px-6 py-4 rounded-sm font-semibold min-h-[56px] text-base uppercase tracking-[0.14em]"
                  >
                    <CalendarCheck className="h-5 w-5" />
                    Schedule a Sweep
                  </Link>
                  <Link
                    href="/contact"
                    onClick={closeMobileMenu}
                    className="flex items-center justify-center gap-2 text-slate-700 hover:text-[#5A3F00] font-medium px-6 py-3 rounded-sm min-h-[44px] text-sm transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Send us a message
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
}
